import React, { useState, useEffect, useRef } from "react";
import nogruntlogo from "../../../Assets/nogrunt-new-ui-logo.svg";
import axios from "axios";
import { Circles } from "react-loader-spinner";
import { API_BACKEND_URL } from "../../../Config/Config";
import { useQueryClient } from "@tanstack/react-query";
import { useNotification } from "../../../hooks";
import {getBrandLogo} from "../../../utils/userDetailsUtils";

import {
  Archive,
  ChevronLeft,
  ChevronRight,
  Container,
  FileCheck2,
  FolderKanban,
  History,
  Home,
  Settings,
  Workflow,
  Upload,
  Zap,
  CornerUpLeft,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import ApiDashboard from "../Dashboard/ApiDashboard";
import Variables from "../Variables/Variables";
import ApiSettings from "../Settings/ApiSettings";
import BulkResultsPanel from "../MainContent/BulkResultsPanel";
import MainContent from "../MainContent/MainContent";
import ApiSuite from "../APISuite/ApiSuite";
import SuiteDetails from "../APISuite/SuiteDetails";
import SuiteResults from "../ApiSuiteResults/SuiteResults";
import SingleSuiteResult from "../ApiSuiteResults/SingleSuiteResult";
import ExecutionTab from "../ExecutionTab/ExecutionTab";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ProvideContext from "../ProvideContext/ProvideContext";
import PerformanceTesting from "../APISuite/PerformanceTesting";
import PerformanceResults from "../APISuite/PerformanceResults";



const NavbarWithSidebar = () => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(true);
  const navigate = useNavigate();
  const [selectedItem, setSelectedItem] = useState("Dashboard");
  const [bulkResultsData, setBulkResultsData] = useState([]);
  const [selectedSuite, setSelectedSuite] = useState(null);
  const [viewSuiteResults, setViewSuiteResults] = useState(null);
  const [viewingSingleResult, setViewingSingleResult] = useState(null);
  const [generationStatus, setGenerationStatus] = useState(null);
  const [isGeneratingTestCases, setIsGeneratingTestCases] = useState(false);
  const [isStatusPopupCollapsed, setIsStatusPopupCollapsed] = useState(false);
  const [viewingPerformanceTest, setViewingPerformanceTest] = useState(null);
  const [viewingPerformanceResults, setViewingPerformanceResults] = useState(null);
  const statusIntervalRef = useRef(null);
  const queryClient = useQueryClient();
  const notify = useNotification();

 const userDetails = localStorage.getItem("userDetail");
const user = userDetails ? JSON.parse(userDetails) : null;
const logoToShow = getBrandLogo(nogruntlogo);

  // Test case generation status polling
  const startGenerationStatusPolling = () => {
    setIsGeneratingTestCases(true);
    setIsStatusPopupCollapsed(false);

    if (statusIntervalRef.current) {
      clearInterval(statusIntervalRef.current);
    }

    statusIntervalRef.current = setInterval(() => {
      axios
        .get(`${API_BACKEND_URL}/testcase/status?companyid=${user.companyid}`)
        .then((response) => {
          const newStatus = response.data;
          setGenerationStatus(newStatus);

          if (newStatus && !newStatus.isGenerating) {
            setIsGeneratingTestCases(false);
            clearInterval(statusIntervalRef.current);
            statusIntervalRef.current = null;

            queryClient.invalidateQueries(["latestAPIResults"]);
            notify("Test case generation completed successfully!", "success");
          }
        })
        .catch((error) => {
          console.error("Error fetching generation status:", error);
          setIsGeneratingTestCases(false);
          clearInterval(statusIntervalRef.current);
          statusIntervalRef.current = null;
          notify("Error checking generation status", "error");
        });
    }, 5000);
  };

  const stopGenerationStatusPolling = () => {
    if (statusIntervalRef.current) {
      clearInterval(statusIntervalRef.current);
      statusIntervalRef.current = null;
    }
    setIsGeneratingTestCases(false);
    setGenerationStatus(null);
  };

  useEffect(() => {
    return () => {
      if (statusIntervalRef.current) {
        clearInterval(statusIntervalRef.current);
      }
    };
  }, []);

  const menuItems = [
    { name: "Dashboard", icon: <Home size={20} /> },
    { name: "Provide Context", icon: <Upload size={20} /> },
    { name: "Collections", icon: <Archive size={20} /> },
    { name: "Chains", icon: <Workflow size={20} /> },
    { name: "Test Cases", icon: <Zap size={20} /> },
    { name: "History", icon: <History size={20} /> },
    { name: "Environments", icon: <Container size={20} /> },
    { name: "Manage API Suites", icon: <FolderKanban size={20} /> },
    { name: "Test Case Results", icon: <FileCheck2 size={20} /> },
  ];

  const toggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  const handleItemClick = (itemName) => {
    setSelectedItem(itemName);
    if (itemName !== "Manage API Suites") {
      setSelectedSuite(null);
    }
    setIsSidebarCollapsed(true);
  };

  const handleBackToSuites = () => {
    setSelectedSuite(null);
  };

  // Function to render the correct component
  const renderMainContent = () => {
    const components = {
      Dashboard: <ApiDashboard />,
      "Provide Context": <ProvideContext />,
      Collections: (
        <MainContent
          tab="collections"
          startGenerationStatusPolling={startGenerationStatusPolling}
        />
      ),
      Chains: (
        <MainContent tab="chains" startGenerationStatusPolling={startGenerationStatusPolling} />
      ),
      "Test Cases": <ExecutionTab />,
      History: (
        <MainContent tab="history" startGenerationStatusPolling={startGenerationStatusPolling} />
      ),
      Environments: <Variables />,
      "Manage API Suites":
        selectedSuite && viewingPerformanceResults ? (
          <PerformanceResults
            suite={viewingPerformanceResults}
            onBack={() => setViewingPerformanceResults(null)}
          />
        ) : selectedSuite && viewingPerformanceTest ? (
          <PerformanceTesting
            suite={viewingPerformanceTest}
            onBack={() => setViewingPerformanceTest(null)}
            onViewPerformanceResults={(suite) => setViewingPerformanceResults(suite)}
          />
        ) : selectedSuite && viewingSingleResult ? (
          <SingleSuiteResult
            suiteResult={viewingSingleResult}
            onBack={() => setViewingSingleResult(null)}
          />
        ) : selectedSuite && viewSuiteResults ? (
          <SuiteResults
            suite={viewSuiteResults}
            onBack={() => setViewSuiteResults(null)}
            onViewSingleResult={(result) => setViewingSingleResult(result)}
          />
        ) : selectedSuite ? (
          <SuiteDetails
            suite={selectedSuite}
            onBack={handleBackToSuites}
            onViewResults={(suite) => setViewSuiteResults(suite)}
            onPerformanceTest={(suite) => setViewingPerformanceTest(suite)}
          />
        ) : (
          <ApiSuite onSuiteSelect={setSelectedSuite} />
        ),
      "Test Case Results": (
        <BulkResultsPanel
          setBulkResultsData={setBulkResultsData}
          bulkResultsData={bulkResultsData}
        />
      ),
      Settings: <ApiSettings />,
    };

    return components[selectedItem] || components.Dashboard;
  };

  return (
    <main className="h-screen w-screen bg-gradient-to-br from-slate-50 via-white to-slate-50 overflow-hidden">
      <style>{`
        
        * {
          font-family: 'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        }

        .sidebar-scroll::-webkit-scrollbar {
          width: 6px;
        }

        .sidebar-scroll::-webkit-scrollbar-track {
          background: transparent;
        }

        .sidebar-scroll::-webkit-scrollbar-thumb {
          background: #d1d5db;
          border-radius: 3px;
        }

        .sidebar-scroll::-webkit-scrollbar-thumb:hover {
          background: #9ca3af;
        }

        .nav-item-active {
          background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
          color: white;
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
        }

        .nav-item-hover {
          background: rgba(59, 130, 246, 0.08);
        }

        .transition-smooth {
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }

        @keyframes slideInStatus {
          from {
            opacity: 0;
            transform: translateY(-12px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .status-popup {
          animation: slideInStatus 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
      `}</style>

      <div className="flex h-full gap-0">
        {/* Sidebar */}
        <aside
          className={`fixed left-0 top-0 h-screen bg-white border-r border-gray-200 flex flex-col transition-smooth ${
            isSidebarCollapsed ? "w-20" : "w-64"
          }`}
          style={{ zIndex: 1000 }}
        >
          {/* Header */}
          <div className="h-16 flex items-center justify-between px-4 border-b border-gray-100 bg-white">
            {!isSidebarCollapsed && (
              <img src={logoToShow} alt="Logo" className="h-8 w-auto transition-smooth" />
            )}
            <button
              onClick={toggleSidebar}
              className="flex items-center justify-center w-9 h-9 rounded-lg hover:bg-gray-100 transition-smooth text-gray-600 hover:text-gray-900 cursor-pointer"
            >
              {isSidebarCollapsed ? <ChevronRight size={20} /> : <ChevronLeft size={20} />}
            </button>
          </div>

          {/* Menu Items */}
          <nav className="flex-1 overflow-y-auto sidebar-scroll px-3 py-4">
            <div className="space-y-1">
              {menuItems.map((item) => (
                <button
                  key={item.name}
                  onClick={() => handleItemClick(item.name)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-smooth font-medium text-sm cursor-pointer ${
                    selectedItem === item.name
                      ? "nav-item-active"
                      : "text-gray-700 hover:nav-item-hover"
                  }`}
                  title={item.name}
                >
                  <span className="flex-shrink-0 flex items-center justify-center">
                    {item.icon}
                  </span>
                  {!isSidebarCollapsed && (
                    <span className="text-sm font-500 truncate">{item.name}</span>
                  )}
                </button>
              ))}
            </div>
          </nav>

          {/* Settings Footer */}
          <div className="border-t border-gray-100 p-3 space-y-1">
            <button
              onClick={() => handleItemClick("Settings")}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-smooth font-medium text-sm cursor-pointer ${
                selectedItem === "Settings"
                  ? "nav-item-active"
                  : "text-gray-700 hover:nav-item-hover"
              }`}
              title="Settings"
            >
              <span className="flex-shrink-0 flex items-center justify-center">
                <Settings size={20} />
              </span>
              {!isSidebarCollapsed && <span className="text-sm font-500">Settings</span>}
            </button>
            <button
              onClick={() => navigate("/new")}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-smooth font-medium text-sm cursor-pointer text-gray-700 hover:nav-item-hover`}
              title="Back to Dashboard"
            >
              <span className="flex-shrink-0 flex items-center justify-center">
                <CornerUpLeft size={20} />
              </span>
              {!isSidebarCollapsed && <span className="text-sm font-500">Back to Dashboard</span>}
            </button>
          </div>
        </aside>

        {/* Main Content */}
        <main
          className={`flex-1 transition-smooth ${
            isSidebarCollapsed ? "ml-20" : "ml-64"
          } h-screen overflow-hidden`}
        >
          <div className="h-full w-full overflow-y-auto px-8 py-8">{renderMainContent()}</div>
        </main>
      </div>

      {/* Mounted here rather than inside a view: every tab renders through this component, and a
          toast fired from a view without a container is silently dropped. */}
      <ToastContainer position="top-right" autoClose={4000} newestOnTop />

      {/* Status Popup */}
      {isGeneratingTestCases && (
        <div className="fixed bottom-6 right-6 z-50 status-popup">
          <div className="bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden backdrop-blur-xl max-w-sm">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-4 py-3 border-b border-blue-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <div className="absolute inset-0 rounded-full bg-blue-300 animate-pulse opacity-40"></div>
                    <Circles height="18" width="18" color="#3b82f6" ariaLabel="generating" />
                  </div>
                  <h3 className="text-sm font-semibold text-gray-900">Generating Test Cases</h3>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setIsStatusPopupCollapsed(!isStatusPopupCollapsed)}
                    className="p-1 hover:bg-blue-100 rounded-md transition-smooth cursor-pointer"
                    title={isStatusPopupCollapsed ? "Expand" : "Collapse"}
                  >
                    <ChevronLeft
                      size={16}
                      className={`transition-smooth ${
                        isStatusPopupCollapsed ? "rotate-90" : "-rotate-90"
                      }`}
                    />
                  </button>
                  <button
                    onClick={stopGenerationStatusPolling}
                    className="p-1 hover:bg-red-100 rounded-md transition-smooth text-gray-500 hover:text-red-600 cursor-pointer"
                    title="Stop"
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M6 4h12v12H6z" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>

            {/* Content */}
            {!isStatusPopupCollapsed && (
              <div className="px-4 py-3 space-y-3">
                {generationStatus && (
                  <>
                    {generationStatus.currentEndpoint && (
                      <div className="space-y-1">
                        <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                          Processing
                        </p>
                        <p className="text-xs font-mono text-blue-600 bg-blue-50 p-2 rounded break-all">
                          {generationStatus.currentEndpoint}
                        </p>
                      </div>
                    )}
                    {generationStatus.progress && (
                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                            Progress
                          </p>
                          <span className="text-xs font-bold text-blue-600">
                            {generationStatus.progress}%
                          </span>
                        </div>
                        <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-blue-500 to-indigo-500 transition-all duration-300"
                            style={{
                              width: `${generationStatus.progress}%`,
                            }}
                          ></div>
                        </div>
                      </div>
                    )}
                  </>
                )}
                <p className="text-xs text-gray-500 pt-2 border-t border-gray-100">
                  This may take a few minutes...
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </main>
  );
};

export default NavbarWithSidebar;
